// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { type PortfolioPerformanceData } from '@shared/data-access/interfaces/api/brokerage/portfolio';
import { type FC, useMemo } from 'react';
import { usePerformanceTable } from '../hooks';
import styles from './performance-table.module.css';
import { SectionContainer } from '@shared/ui-kit/components';
import { useTranslation } from 'react-i18next';
import { Translation } from '@shared/ui-kit/providers/translation-provider/translation';

export interface PerformanceTableProps {
  readonly data: PortfolioPerformanceData[];
}

export const PerformanceTable: FC<PerformanceTableProps> = ({ data }) => {
  const { performance } = usePerformanceTable(data);
  const { t } = useTranslation();

  const HEADER_COLUMNS_NAME = useMemo(
    () => [
      t('common.name', { ns: 'common' }),
      t('glossary.january', { ns: 'glossary' }),
      t('glossary.february', { ns: 'glossary' }),
      t('glossary.march', { ns: 'glossary' }),
      t('glossary.april', { ns: 'glossary' }),
      t('glossary.may', { ns: 'glossary' }),
      t('glossary.june', { ns: 'glossary' }),
      t('glossary.july', { ns: 'glossary' }),
      t('glossary.august', { ns: 'glossary' }),
      t('glossary.september', { ns: 'glossary' }),
      t('glossary.october', { ns: 'glossary' }),
      t('glossary.november', { ns: 'glossary' }),
      t('glossary.december', { ns: 'glossary' }),
    ],
    [t],
  );

  const headers = useMemo(
    () =>
      Array.from(HEADER_COLUMNS_NAME, header => (
        <th key={header} className={styles['table-header']}>
          <span>{header}</span>
        </th>
      )),
    [HEADER_COLUMNS_NAME],
  );

  const body = useMemo(
    () =>
      Object.entries(performance).map(([year, months]) => (
        <tr key={year}>
          <td className={styles['table-row']}>
            <span>{year}</span>
          </td>
          {Object.entries(months).map(([month, { portfolio_return }]) => (
            <td key={`${year}/${month}`} className={styles['table-row']}>
              {isNaN(portfolio_return) ? (
                '-'
              ) : (
                <span>{(portfolio_return * 100).toFixed(2)}%</span>
              )}
            </td>
          ))}
        </tr>
      )),
    [performance],
  );

  return (
    <SectionContainer
      title={<Translation i18nKey="dashboard.monthly-returns" ns="dashboard" />}
    >
      <table className={styles.table}>
        {data.length ? (
          <thead>
            <tr>{headers}</tr>
          </thead>
        ) : null}
        <tbody>
          {data.length ? (
            body
          ) : (
            <tr>
              <td
                colSpan={13}
                rowSpan={3}
                className="p-4 text-center text-lg font-semibold text-neutral-500"
              >
                <Translation
                  i18nKey="dashboard.there-is-no-data-available"
                  ns="dashboard"
                />
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </SectionContainer>
  );
};
